import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/db";

/**
 * @typedef {import('@/types/schema').Appointment} Appointment
 */

/**
 * Custom hook to fetch and filter appointments based on user role and permissions.
 * Handles the logic for:
 * 1. Fetching own appointments
 * 2. Fetching clinic appointments
 * 3. Applying anonymization rules (hiding patient details for non-admins)
 * 4. Filtering by search term and active mode (clinic vs personal)
 * 
 * @param {string} doctorId - ID of the current user
 * @param {string} activeMode - Current view mode (clinic ID, 'personal', 'everything')
 * @param {string} searchTerm - Search query
 * @param {Array} allPatients - List of all patients (for search filtering)
 * @returns {Array<Appointment>} parameters.appointments - Sorted and filtered appointments
 */
export function useAppointmentsData(doctorId, activeMode, searchTerm, allPatients) {

    const rawAppointments = useLiveQuery(async () => {
        if (!doctorId) return [];

        // 1. Get user's own appointments (Always visible, never anonymized)
        const ownApps = await db.appointments.where('doctor_id').equals(doctorId).toArray();

        // 2. Get clinics user is a member of with roles
        const memberships = await db.clinic_members
            .where('user_id').equals(doctorId)
            .filter(m => m.is_active !== false)
            .toArray();

        // 3. Identify clinics where user has access to see others' appointments
        const allowedClinicIds = memberships.map(m => m.clinic_id);

        // 4. Fetch all appointments for these clinics
        let clinicApps = [];
        if (allowedClinicIds.length > 0) {
            const targetClinicIds = Array.from(new Set([
                ...allowedClinicIds,
                ...allowedClinicIds.map(id => String(id))
            ]));

            // Note: This might fetch duplicates if an appointment is both 'mine' and 'in my clinic',
            // but the Map deduplication below handles it.
            clinicApps = await db.appointments
                .where('clinic_id')
                .anyOf(targetClinicIds)
                .toArray();
        }

        // 5. Merge and Process Anonymization
        const allAppsMap = new Map();

        // Helper to determine active role for a clinic
        const getRole = (cId) => memberships.find(m => m.clinic_id === cId)?.role;

        // Add own appointments first (Master copy - fully visible)
        ownApps.forEach(app => {
            allAppsMap.set(app.id, { ...app, is_anonymized: false });
        });

        // Add clinic appointments with permission checks
        clinicApps.forEach(app => {
            if (allAppsMap.has(app.id)) return; // Already added as own

            const role = getRole(app.clinic_id);
            const isMyAppointment = app.doctor_id === doctorId;

            // Anonymization Logic:
            // - If it's MY appointment -> Visible
            // - If I am Admin or Receptionist or FTE Doctor -> Visible
            // - If I am Consultant -> ANONYMIZED
            let shouldAnonymize = false;
            if (!isMyAppointment) {
                if (role === 'consultant') {
                    shouldAnonymize = true;
                }
            }

            allAppsMap.set(app.id, { ...app, is_anonymized: shouldAnonymize });
        });

        return Array.from(allAppsMap.values()).sort((a, b) => {
            // Prioritize 'scheduled'
            const isAScheduled = a.status === 'scheduled';
            const isBScheduled = b.status === 'scheduled';

            if (isAScheduled && !isBScheduled) return -1;
            if (!isAScheduled && isBScheduled) return 1;

            const dateA = new Date(a.date_time);
            const dateB = new Date(b.date_time);

            if (isAScheduled) {
                return dateA - dateB; // Async: Soonest first
            } else {
                return dateB - dateA; // Past: Most recent first
            }
        });
    }, [doctorId]) || [];

    // Filter by Mode and Search Term
    const filteredAppointments = rawAppointments.filter(app => {
        // 1. Filter by Mode
        if (activeMode === 'personal') {
            // Personal Mode: My apps, no clinic OR explicitly 'personal' clinic
            if (!(app.doctor_id === doctorId && (!app.clinic_id || app.clinic_id === 'personal'))) {
                return false;
            }
        } else if (activeMode !== 'everything') {
            // Specific Clinic Mode
            if (app.clinic_id !== activeMode) {
                return false;
            }
        }

        // 2. Filter by Search Term
        if (!searchTerm) return true;

        const term = searchTerm.toLowerCase();

        // If anonymized, we can't search by patient PII
        if (app.is_anonymized) return false;

        const patient = allPatients.find(p => String(p.id) === String(app.patient_id));
        const nameMatch = patient?.name?.toLowerCase().includes(term) || false;
        const phoneMatch = patient?.phone?.includes(term) || false;
        const noteMatch = app.notes?.toLowerCase().includes(term) || false;

        return nameMatch || phoneMatch || noteMatch;
    });

    return filteredAppointments;
}
